﻿import hash from 'hash-sum';
import { firstValueFrom, Subject } from 'rxjs';

import { AuthenticationResult } from '@pressreader/authentication-types';
import { error, warn } from '@pressreader/logger';
import { cookies, getParsedJwt } from '@pressreader/utils';

import { GIFTED_ACCESS_TOKEN } from '../constants';

import { apiSiteSignIn, initAuthentication, refreshBearerToken, saveAuthorizationTickets } from './authentication.api';
import { getInterfaceLanguage } from './interfacelanguage';
import { type ClockMarks, elapsedSince, hasServerClock, serverNow, takeClockMarks } from './server.clock';
import { getToken } from './services';

declare const window: Window & {
    _preload: Preload;
};

type EventType = 'online' | 'offline' | 'tokenExpired' | 'tokenUpdated' | 'identityChanged';

const TokenFieldName = 'ab'; // token
const UserKeyFieldName = 'sub'; // userKey
const ExpirationFieldName = 'exp'; // expiration
const IssuedAtFieldName = 'iat'; // issued at
const NotBeforeFieldName = 'nbf'; // not before — read only as a stand-in for `iat`, see `getMintedLifetime`
// const SponsorIdFieldName = 'ag'; // sponsorId

/** Browser `setTimeout` delays are a signed 32 bit integer; anything larger overflows to a negative delay. */
const MAX_TIMEOUT_MS = Math.pow(2, 31) - 1;
/** How long before expiration the token is refreshed. */
const REFRESH_LEAD_MS = 60 * 1000;
/** Closest together two refresh attempts may be scheduled: the floor on the timer, and the retry delay after a
 *  failed attempt. See `updateTokenWithTimeout` and `_scheduleRefresh`. */
const MIN_REFRESH_MS = 30 * 1000;
/** Assumed lifetime of a token that carries no usable expiration. */
const DEFAULT_LIFETIME_MS = 5 * 60 * 1000;
/** How long receipt alone vouches for a token, while the server's time is still unknown. See `isBearerTokenUsable`. */
const RECENTLY_ISSUED_MS = 60 * 1000;

/** Identity of every session that is not signed in as a user (no token yet, anonymous or malformed `sub`). */
const ANONYMOUS_IDENTITY_KEY = 'anonymous';

interface AuthEvent {
    subject: Subject<void>;
    subscriptions: {
        callback(): void;
        unsubscribe(): void;
    }[];
}

interface Preload {
    confirmed: boolean;
}

interface TokenDto {
    Token: string;
    UserKey: string;
    BearerToken: string;
    ExpiresIn: number;
}

const events: Partial<Record<EventType, AuthEvent>> = {};
let _userKey: number | null = null;
let _bearerToken: string | null = null;
/**
 * Life `_bearerToken` had when it arrived, on the server's clock. See `_recordTokenClock`.
 *
 * The zero initializer is a safe starting point rather than an arbitrary one: paired with epoch marks below it makes
 * `tokenRemainingMs()` deeply negative, so nothing can be vouched for before a token has actually been recorded.
 * `isBearerTokenUsable` also rejects on the `_bearerToken` identity check first, so this is the second of two guards.
 */
let _tokenLifetimeAtReceiptMs = 0;
/** When it arrived, as marks to count forward from rather than an absolute time to compare against. */
let _tokenClockMarks: ClockMarks = { monotonic: 0, wall: 0 };
let _useGeoLocation = false;
let _updateTokenTimeoutId: number | null = null;
/**
 * Monotonic counter of scheduled refresh attempts, so a failing attempt can tell whether it is still the
 * current one. Same shape and same reason as `_identityGeneration`: only the newest attempt owns
 * `_updateTokenTimeoutId`, and a stale one that retried anyway would strand the timer the newest one armed.
 */
let _refreshAttempt = 0;
let isOnline = false;
let _initialized = false;
let _jwtHash = '';
let isPending = true;
let _emailVerified = false;
// Identity clock: `_identityGeneration` counts identity transitions of the token, `_lastIdentityKey`
// is the identity that generation was minted for, `_previousIdentityKey` is the one the generation
// before it was minted for (the from-key of the most recent transition), and `_identitySeeded` tells
// the boot token (which establishes the identity, it does not change it) from every token that follows.
let _identityGeneration = 0;
let _lastIdentityKey = ANONYMOUS_IDENTITY_KEY;
let _previousIdentityKey = ANONYMOUS_IDENTITY_KEY;
let _identitySeeded = false;

const bearerTokenSubject = new Subject<string>();

bearerTokenSubject.subscribe({
    next() {
        isPending = false;
    },
    error() {
        isPending = false;
    },
});
let authPromise = firstValueFrom(bearerTokenSubject);

function resetPromise() {
    if (isPending) {
        // No need to reset: the promise is still unresolved.
        return;
    }
    isPending = true;
    authPromise = firstValueFrom(bearerTokenSubject);
}

const setGiftedAccessToken = (token: string | undefined) => {
    if (token) {
        cookies.set(GIFTED_ACCESS_TOKEN, token);
    }
};

async function initAuthTickets() {
    try {
        return await initializeAuthAndPreload();
    } catch (reason) {
        return _onTokenFailed(reason);
    }
}

//TODO Check init.help.page.ts for usage:
// const hostName = window.location.hostname;
// const ticket = await activationProvider.getTicketByHost(hostName);
// svcAuth.online(ticket);
function online(): boolean;
function online(ticket: string): void;
function online(...params: unknown[]): boolean | void {
    // go online
    if (!arguments.length) {
        return isOnline;
    }
    // Keep for backward compatibility for init.help.page.ts
    const ticket = params[0] as string;
    getToken<TokenDto>([ticket], getInterfaceLanguage()).then(data => _onTokenReceived(data.BearerToken), _onTokenFailed);
}

function _onTokenFailed(reason: unknown) {
    resetPromise();
    bearerTokenSubject.error(reason);
}

/**
 * Advances the identity clock to the token that has just been stored.
 *
 * Called from `_onTokenReceived` before any subscriber is notified, so every `online`/`tokenUpdated`
 * callback observes the generation of the token it is reacting to. The generation moves only when the
 * normalized identity changes: a token rotation or a claims-only change keeps it, and the first token
 * of the session (the one `initializeAuthAndPreload` produces) only seeds the clock, so a session
 * starts at generation 0 whether it boots anonymous or signed in.
 *
 * A transition also records the identity being LEFT (`_previousIdentityKey`, read back through
 * `previousIdentityKey()`), so the from-key of the event is a fact of the clock rather than something every
 * subscriber has to mirror for itself. The seed does not touch it: it establishes an identity, it leaves none.
 *
 * Returns whether this token was an identity TRANSITION — what `_onTokenReceived` needs to decide whether to
 * publish, independently of the claims hash, and what gates the `identityChanged` event it fires.
 */
function _updateIdentityClock() {
    const currentKey = identityKey();
    const isTransition = _identitySeeded && currentKey !== _lastIdentityKey;
    _identitySeeded = true;
    if (isTransition) {
        // Captured BEFORE `_lastIdentityKey` is overwritten — it is the identity this transition leaves.
        _previousIdentityKey = _lastIdentityKey;
        _identityGeneration++;
    }
    _lastIdentityKey = currentKey;

    return isTransition;
}

function _onTokenReceived(bearer: string) {
    const jwtToken = getParsedJwt(bearer);
    if (!jwtToken) {
        return;
    }
    delete jwtToken[TokenFieldName];
    delete jwtToken[ExpirationFieldName];
    const newJwtHash = hash(jwtToken);

    _userKey = Number(jwtToken[UserKeyFieldName]);
    _bearerToken = bearer;
    _recordTokenClock(bearer);

    const identityTransitioned = _updateIdentityClock();

    resetPromise();

    if (!isOnline) {
        isOnline = true;
        // token was updated, no need to notify status update
        _fireCallbacks('online');
    }

    // Re-armed for EVERY token, outside the publication gate below. Whether the refresh timer is scheduled has
    // nothing to do with whether the claims changed, and tying the two together silently disabled refresh: the
    // hash strips `ab` and `exp`, and the tokens this service issues carry no other per-mint claim (`ap` is the
    // session's creation time, stable across rotations), so a rotation hashes EQUAL — the timer was armed once at
    // boot, fired once, and was never re-armed for the rest of the session.
    updateTokenWithTimeout();

    // An identity transition ALWAYS publishes, whatever the claims hash says. The hash is a `hash-sum` digest,
    // and digests collide: `{ iss: 'pressreader', sub: '11211', aud: 'pressreader' }` and the same claims with
    // `sub: '1209522'` both hash to 01d8597c, so on that account switch the hash comparison alone would stay
    // silent and every identity consumer downstream (the `identityChanged` publication below, and with it the
    // Redux identity bridge and the RTK Query cache reconcile) would keep serving the previous user's data.
    // Rotation silence is unaffected: the same identity with the same claims still hashes equal and still does
    // not publish.
    if (_jwtHash !== newJwtHash || identityTransitioned) {
        _jwtHash = newJwtHash;
        _fireCallbacks('tokenUpdated');
        // The identity transition itself, published where it is DECIDED so no subscriber has to re-derive it by
        // diffing `tokenUpdated` events. Fires for a real transition only — never the boot seed, never a
        // same-identity rotation (`_updateIdentityClock` owns that distinction) — and always after `tokenUpdated`,
        // which fires for every published token, transitions included (`identityTransitioned` is what forces this
        // publish past the hash compare).
        if (identityTransitioned) {
            _fireCallbacks('identityChanged');
        }
    }

    bearerTokenSubject.next(bearer);
}

function getExpirationTime(bearerToken: string) {
    const jwtToken = getParsedJwt(bearerToken);
    return jwtToken ? Number(jwtToken[ExpirationFieldName]) : 0;
}

/**
 * Milliseconds of life a freshly received token was minted with, read out of the token itself.
 *
 * A second way to sidestep the device clock, used by `_recordTokenClock` only when no server `Date` has been
 * seen yet: `exp` and `iat`/`nbf` are stamped by the same issuing server, so their difference is a pure
 * duration and survives a device whose clock is hours off.
 *
 * Note what it is NOT: the life a token has LEFT. It is the span the token was minted with, and the two are
 * only the same number at the instant of arrival — which is the one moment `_recordTokenClock` runs, and the
 * only condition under which counting it from now is sound. Do not reach for it mid-session, and treat the
 * `Index.cshtml` `window._initAuthResult` preload as the case to check first if this ever goes live: a
 * preloaded token may have been minted before the page loaded, so restarting its full span would overstate
 * what remains.
 *
 * Inert against today's tokens, which carry neither `iat` nor `nbf` — it returns 0 and the caller drops to
 * comparing `exp` against the device clock. Kept because it costs nothing and becomes correct the moment the
 * service starts stamping one. Do NOT press `ap` into service as the issue time: it is when the SESSION was
 * created, not when this token was minted, and on a live token it sits ~294 days before `exp`, so it would read
 * as a 294-day lifetime.
 */
function getMintedLifetime(bearerToken: string) {
    const jwtToken = getParsedJwt(bearerToken);
    if (!jwtToken) {
        return 0;
    }
    const exp = Number(jwtToken[ExpirationFieldName]);
    const issuedAt = Number(jwtToken[IssuedAtFieldName] ?? jwtToken[NotBeforeFieldName]);
    if (!exp || !issuedAt || exp <= issuedAt) {
        return 0;
    }
    return (exp - issuedAt) * 1000;
}

/**
 * Pins how much life the token that just arrived has, and when that reading was taken.
 *
 * Measured ONCE, here, against the best available reading of the issuing server's clock — after which every
 * later question about this token is elapsed-time arithmetic from `_tokenClockMarks` and no absolute clock is
 * consulted again. That is what makes the answers hold up on a device whose clock is wrong in either
 * direction, or gets corrected mid-session.
 *
 * Sources for the lifetime, best first: the server's own `Date` header via `serverNow()`; the token's internal
 * `exp - iat` span if it carries one; `exp` against the device clock, which is the pre-existing behaviour and
 * the reason the floor in `updateTokenWithTimeout` still matters; and for a token with no `exp` at all, a flat
 * `DEFAULT_LIFETIME_MS`.
 *
 * The first source is normally already there when this runs, and by construction rather than by luck: the clock
 * is only ever sampled from a token-issuing response (`trustServerClock` in `fetch.ts`), and `fetchShim` takes
 * that sample as the response resolves — so the response carrying THIS token has already updated the clock by the
 * time `updateAuthorizationTickets` walks down to here. The exception is a token that never came through
 * `fetchShim`: the `Index.cshtml` `window._initAuthResult` preload, where the first refresh is what teaches the
 * module the server's time.
 */
function _recordTokenClock(bearerToken: string) {
    _tokenClockMarks = takeClockMarks();
    const exp = getExpirationTime(bearerToken);
    if (exp && hasServerClock()) {
        _tokenLifetimeAtReceiptMs = exp * 1000 - serverNow();
        return;
    }
    _tokenLifetimeAtReceiptMs = getMintedLifetime(bearerToken) || (exp ? exp * 1000 - Date.now() : DEFAULT_LIFETIME_MS);
}

/** Milliseconds of life the current token has left, counted forward from receipt rather than off a clock. */
function tokenRemainingMs() {
    return _tokenLifetimeAtReceiptMs - elapsedSince(_tokenClockMarks);
}

function updateTokenWithTimeout() {
    if (_updateTokenTimeoutId) {
        window.clearTimeout(_updateTokenTimeoutId);
        _updateTokenTimeoutId = null;
    }
    // Refresh one minute before expiration, but never sooner than MIN_REFRESH_MS. Without that floor a
    // non-positive delay — which is what a token already past `exp` produces, and what a device with no server
    // sample yet and a fast clock produces for every token it is handed — is clamped to 0 by `setTimeout`, and
    // the callback reschedules itself at 0 and spins: one `/refresh` round trip per turn of the event loop.
    // Clamping the delay to >= 0 does NOT prevent this; only a floor does. It is also the backstop that gets
    // the boot window out of trouble: a preloaded token is recorded before any response has been seen, so the
    // first refresh is what teaches this module the server's clock, and 30s is how long that can take.
    // The upper clamp is the browser's 32 bit signed delay limit, past which the delay overflows negative and
    // fires immediately.
    _scheduleRefresh(Math.min(MAX_TIMEOUT_MS, Math.max(MIN_REFRESH_MS, tokenRemainingMs() - REFRESH_LEAD_MS)));
}

/**
 * Arms the single refresh timer, and owns what happens when the attempt it schedules fails.
 *
 * Separate from `updateTokenWithTimeout` so a failure can re-arm the ATTEMPT directly. Routing a retry back
 * through `updateTokenWithTimeout` would only schedule another scheduling pass — the lifetime it would
 * recompute has not changed — costing an extra hop before anything is actually retried.
 *
 * "The single timer" is the invariant, and `_refreshAttempt` is what holds it. Every call here takes the next
 * attempt number, so a callback can tell whether it is still the current attempt by the time its request
 * settles. Without that a slow failure double-arms: attempt 1's request is in flight, a concurrent refresh
 * succeeds and re-arms through `updateTokenWithTimeout` as attempt 2, then attempt 1 rejects and retries —
 * overwriting `_updateTokenTimeoutId` and stranding attempt 2's timer with nothing holding its id. Two timers
 * then refresh on independent schedules, which is the redundant-refresh shape this module exists to remove, and
 * it does not converge on its own: whichever fires first re-arms and leaves the other still pending.
 */
function _scheduleRefresh(delay: number) {
    const attempt = ++_refreshAttempt;
    _updateTokenTimeoutId = window.setTimeout(() => {
        // The chain that keeps this timer alive runs through the response: a token comes back, `_onTokenReceived`
        // re-arms. A rejected refresh breaks it — nothing re-arms, and because the rejection is unobserved no
        // error surfaces either, so the session silently drops back to refreshing per request off `authorized()`.
        // That was survivable while the timer was a one-shot armed at boot; now that it is the recurring refresh
        // mechanism, it is the one failure mode that has to be caught. Retry on the floor.
        updateBearerToken().catch(reason => {
            if (attempt !== _refreshAttempt) {
                // Superseded while this request was in flight. Something newer already owns the timer, so the
                // right move is to do nothing: retrying would replace a schedule armed off a FRESH token with
                // this stale attempt's floor delay, refreshing sooner than the live token needs.
                return;
            }
            error('Authorization: scheduled token refresh failed, retrying', reason);
            _scheduleRefresh(MIN_REFRESH_MS);
        });
    }, delay);
}

/**
 * Refreshes the bearer token, and DROPS the result if the identity moved on while the request was in flight.
 *
 * The request is issued for whoever is signed in when it starts, but nothing about the response says so, and two
 * callers can have one outstanding at any time: the recurring timer in `_scheduleRefresh` and `authorized()`.
 * A sign-out or an account switch in that window makes the reply a token for the PREVIOUS user, and storing it
 * unconditionally reinstates that user — token, `_userKey`, tickets and all — over the one who is actually signed
 * in now, which shows as the wrong account's data. Slow refresh plus a fast switch, no unusual conditions needed.
 *
 * `identityGeneration()` is what makes the two comparable: captured before the await, it names the identity this
 * request was made for, and a different value on return means the answer is about somebody else. Discarding is
 * the safe side — the token that moved the generation is current, already published, and already re-armed the
 * timer through `_onTokenReceived`, so nothing here needs to be recovered.
 *
 * Note what this does NOT reject: a refresh whose own RESULT is a different identity — the server switching the
 * session to another user, or dropping it to anonymous. The generation only moves in `_onTokenReceived`, which
 * runs after this comparison, so that case is unaffected and still publishes normally.
 */
async function updateBearerToken(forceUseGeoLocation?: boolean) {
    const requestedGeneration = identityGeneration();
    const result = await refreshBearerToken(_useGeoLocation || forceUseGeoLocation);
    if (identityGeneration() !== requestedGeneration) {
        warn('Authorization: discarding a token refresh that resolved after the identity changed', {
            requestedGeneration,
            currentGeneration: identityGeneration(),
        });
        return;
    }
    updateAuthorizationTickets(result);
}

/**
 * Whether the current token can be used as is — the check that decides whether `authorized()` costs a round trip.
 *
 * Three questions, in order. Is the recorded lifetime even ABOUT this token? Does that lifetime still have time
 * on it — the normal answer, and one already immune to a wrong device clock because it counts down from a span
 * pinned against the server's. And failing that, was this token handed over so recently that receipt alone
 * vouches for it? That last tier exists only for the window before any server `Date` has been seen, where the
 * lifetime could only be measured against an unverified device clock: one running fast reads every token it is
 * given as already dead, which is a `/refresh` per outgoing request rather than the once-per-lifetime refresh
 * this is meant to be.
 *
 * Bounded, and not a hole in expiry enforcement: it can only extend a token by `RECENTLY_ISSUED_MS`, only while
 * the server's time is unknown, and the server validates every request regardless — the client's answer here is
 * an optimization to avoid a doomed request, never the authority on whether the token is good.
 */
function isBearerTokenUsable(bearerToken: string) {
    // The recorded lifetime and marks describe whichever token arrived MOST RECENTLY, which is not necessarily
    // the one the caller is holding: two overlapping refreshes can resolve a continuation waiting on token A
    // after token B has already overwritten the marks. Vouching for A out of B's lifetime is not a question this
    // function can answer, so it declines and lets the caller refresh. The old `exp`-against-`Date.now()` form
    // got this for free by computing from the same local it returned; measuring against module state does not.
    if (bearerToken !== _bearerToken) {
        return false;
    }
    if (tokenRemainingMs() > 0) {
        return true;
    }
    // The server produced this token moments ago, so the server considers it live whatever the local clock says.
    // Once a `Date` header has been seen the tier above is authoritative and this drops out for good.
    return !hasServerClock() && elapsedSince(_tokenClockMarks) < RECENTLY_ISSUED_MS;
}

/**
 * The bearer token a caller should send, refreshed first if the one in hand is no longer usable.
 *
 * KNOWN FOLLOW-UP, tracked separately and deliberately not addressed here: concurrent callers are not
 * de-duplicated. Each one that finds the token unusable starts its OWN `/refresh`, so five requests arriving after
 * a token dies cost five round trips where one would do. The fix is a shared in-flight promise, and it belongs
 * with the other half of the same gap in `services.ts` (`executeFetchWithRetry` rethrows every status under 500,
 * so a request rejected for an expired token is never retried with the replacement). Together they are the
 * "refresh once, then retry what failed" behaviour this module still does not have.
 *
 * Neither is introduced or worsened by the server-clock work. What this PR removes is the wrong-clock CAUSE of
 * that storm — a device running ahead of the server read every fresh token as already expired, so EVERY request
 * took this branch. The amplifier is left for a change of its own, where the retry semantics can be thought
 * through: a shared promise has to decide what an identity change mid-flight means for its waiters, and a 401
 * retry has to be bounded so a genuine credentials failure cannot loop.
 */
async function authorized() {
    const bearerToken = await authPromise;
    if (isBearerTokenUsable(bearerToken)) {
        return bearerToken;
    }
    await updateBearerToken();
    return await authPromise;
}

function token() {
    const jwtToken = getParsedJwt(bearerToken());
    return jwtToken ? encodeURIComponent(jwtToken[TokenFieldName]) : '';
}

function userKey() {
    return _userKey;
}

/**
 * Identity of the current token, normalized to a comparable string: the user key of a signed-in user,
 * `ANONYMOUS_IDENTITY_KEY` otherwise. Reflects the token, not the (later) committed application state.
 */
function identityKey() {
    return Number(_userKey) > 0 ? String(_userKey) : ANONYMOUS_IDENTITY_KEY;
}

/**
 * The identity key the clock held before its most recent transition.
 *
 * Inside an `identityChanged` callback it pins the FROM-key of exactly that event — the clock is updated before
 * the callbacks fire — so a subscriber reads the whole transition (from, to, generation) off the clock instead of
 * keeping a mirror of its own, which can only ever be primed at subscription time and so reports the identity in
 * effect then rather than the one this token replaced. Stays at `ANONYMOUS_IDENTITY_KEY` until the first
 * transition: the boot token seeds the identity, it leaves none behind.
 */
function previousIdentityKey() {
    return _previousIdentityKey;
}

/**
 * Monotonic counter of the identity transitions the token has been through, starting at 0 for the boot
 * identity. Capture it before an asynchronous operation and compare it after to tell whether the result
 * still belongs to the identity that requested it.
 *
 * `updateBearerToken` is this module's own instance of that pattern, and the reason the counter has to be
 * monotonic rather than a "did the key change" flag: an A -> B -> A round trip lands back on the same identity
 * key, so a key comparison would call a stale in-flight response current.
 */
function identityGeneration() {
    return _identityGeneration;
}

function bearerToken() {
    return _bearerToken;
}

function bind(eventType: EventType, callback: () => void) {
    let event = events[eventType];
    if (!event) {
        event = events[eventType] = {
            subject: new Subject<void>(),
            subscriptions: [],
        };
    }
    if (event.subscriptions.map(s => s.callback).indexOf(callback) < 0) {
        const { unsubscribe } = event.subject.subscribe(callback);
        event.subscriptions.push({ callback, unsubscribe });
    }
}

function unbind(eventType: EventType, callback: () => void) {
    const event = events[eventType];
    if (!event) {
        return;
    }
    const idx = event.subscriptions.findIndex(s => s.callback === callback);
    if (idx < 0) {
        return;
    }
    event.subscriptions[idx].unsubscribe();
    event.subscriptions.splice(idx, 1);
}

function _fireCallbacks(eventType: EventType) {
    const event = events[eventType];
    if (event) {
        event.subject.next();
    }
}

async function initializeAuthAndPreload() {
    if (_initialized) {
        return;
    }
    _initialized = true;
    let result: AuthenticationResult | undefined;
    const documentUrl = new URL(document.location.href);
    const token = documentUrl.searchParams.get('signInToken');
    if (token) {
        try {
            result = await apiSiteSignIn(token, true);
            const emailVerifiedParam = documentUrl.searchParams.get('emailVerified');
            _emailVerified = emailVerifiedParam === '1';
        } catch (e) {
            error('EmailVerified: siteSignInResult error', e);
        }
    }
    if (!result) {
        // Try preloaded auth result from inline script in Index.cshtml
        // TODO(171724): Strengthen preload fallback traceability so auth-preload regressions are explicit and easier to diagnose (PR 49145 follow-up).
        const preloadedAuth = (window as any)._initAuthResult as Promise<AuthenticationResult> | undefined;
        if (preloadedAuth) {
            delete (window as any)._initAuthResult;
            try {
                result = await preloadedAuth;
            } catch {
                // Preload failed, fall through to the regular fallback init path
            }
        }
        if (!result) {
            // Keep the non-preloaded fallback aligned with the Index.cshtml preload path
            // that spins up "(window as any)._initAuthResult".
            result = await initAuthentication();
        }
        if (result.useGeoLocation === 1) {
            result = await refreshBearerToken(true);
        }
    }
    updateAuthorizationTickets(result);
}

function updateAuthorizationTickets(response: AuthenticationResult) {
    _useGeoLocation = response.useGeoLocation === 1;
    saveAuthorizationTickets(response.tickets);
    _onTokenReceived(response.bearerToken);
}

function isEmailVerified() {
    return _emailVerified;
}

export {
    ANONYMOUS_IDENTITY_KEY,
    authorized,
    bearerToken,
    bind,
    identityGeneration,
    identityKey,
    initAuthTickets,
    isEmailVerified,
    online,
    previousIdentityKey,
    setGiftedAccessToken,
    token,
    unbind,
    updateAuthorizationTickets,
    updateBearerToken,
    userKey,
};
